import {
  Alert,
  Badge,
  Button,
  DescriptionList,
  FormSectionLabel,
  LoadingState,
  Modal,
  Table,
  Text,
} from "@marekl/adminer/components";
import type { Dayjs } from "@marekl/adminer/datetime";
import { formatDate, formatDateTime } from "@marekl/adminer/format";
import { MusicPanel } from "../MusicPanel";
import { MusicPlayer } from "../MusicPlayer";
import type { ComboRef } from "../../lib/rulesDocument";
import { comboLabel } from "../../lib/rulesDocument";
import type { MusicAudience } from "../../lib/musicState";
import { musicBadge } from "../../lib/musicState";
import { MusicFileSummary } from "../MusicFileSummary";
import { RegisteredTick } from "../RegisteredTick";
import { formatTimeRange } from "../../lib/rulesLabels";
import { ENTRY_STATUS_LABELS, ENTRY_STATUS_VARIANT } from "../../lib/competitionState";
import type { Entry, EntryCompetitor } from "../../lib/queries/entries";
import {
  entryMusicUrl,
  recordingFileUrl,
  useUploadEntryMusic,
} from "../../lib/queries/music";
import styles from "./EntryDetailModal.module.css";
import { PersonLink } from "../PersonDetail";

/**
 * One přihláška in full: what was entered, who competes it, and what
 * will play.
 *
 * It exists because four surfaces asked the same question and each
 * answered a different part of it — the club's entry list showed the
 * roster but not the rules, the organizer's Hudba page showed the
 * recording but not the roster, and the start list showed neither. The
 * modal is the one place that answers all of it, so those surfaces open
 * this rather than growing columns.
 *
 * It is a READ surface with one exception: the recording, which follows
 * its own window (see MusicPanel) and is the thing people open an entry
 * to fix. Editing the entry itself stays in the wizard.
 */
export function EntryDetailModal({
  entry,
  combo,
  canUploadMusic = false,
  musicDisabledReason,
  musicDeadline,
  audience = "team",
  onClose,
}: {
  /** null while the caller is still resolving it — the start list holds
   * only a row id and fetches the entry when the dialog opens. */
  entry: Entry | null;
  /** The resolved combination. Undefined until the rule-set document
   * loads, which is why every label falls back to the raw id rather than
   * waiting for it. */
  combo?: ComboRef;
  canUploadMusic?: boolean;
  musicDisabledReason?: string;
  /** The effective uzávěrka hudby. Named beside a missing file, so a club
   * reads the upload as due by a date rather than as required now. */
  musicDeadline?: Dayjs;
  audience?: MusicAudience;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      title="Detail přihlášky"
      onClose={onClose}
      width={640}
      footer={<Button onClick={onClose}>Zavřít</Button>}
    >
      {entry ? (
        <EntryDetailBody
          entry={entry}
          combo={combo}
          canUploadMusic={canUploadMusic}
          musicDisabledReason={musicDisabledReason}
          musicDeadline={musicDeadline}
          audience={audience}
        />
      ) : (
        <LoadingState />
      )}
    </Modal>
  );
}

function EntryDetailBody({
  entry,
  combo,
  canUploadMusic,
  musicDisabledReason,
  musicDeadline,
  audience,
}: {
  entry: Entry;
  combo?: ComboRef;
  canUploadMusic: boolean;
  musicDisabledReason?: string;
  musicDeadline?: Dayjs;
  audience: MusicAudience;
}) {
  const title = combo ? comboLabel(combo) : "Kombinace";
  const limits = combo
    ? formatTimeRange(combo.category.time_min_seconds, combo.category.time_max_seconds)
    : null;

  return (
    <div className={styles.body}>
      {/* The result-list key first: it is how the federation names this
          performance, and it is what the reader came to confirm. */}
      <div className={styles.heading}>
        <div className={styles.combo}>{title}</div>
        <Text as="div" variant="muted">
          {entry.team_name ?? "—"}
        </Text>
      </div>

      <DescriptionList>
        <DescriptionList.Item label="Číslo přihlášky">
          <Text variant="number">{entry.entry_number}</Text>
        </DescriptionList.Item>
        <DescriptionList.Item label="Stav">
          <Badge variant={ENTRY_STATUS_VARIANT[entry.status]}>
            {ENTRY_STATUS_LABELS[entry.status]}
          </Badge>
        </DescriptionList.Item>
        {limits && (
          <DescriptionList.Item label="Časový limit">{limits}</DescriptionList.Item>
        )}
        <DescriptionList.Item label="Podáno">
          {formatDateTime(entry.created_at)}
        </DescriptionList.Item>
        {entry.status === "withdrawn" && (
          <DescriptionList.Item label="Odhlášeno">
            {entry.withdrawn_at ? formatDateTime(entry.withdrawn_at) : "—"}
            {entry.withdrawn_note && (
              <Text as="div" variant="muted">
                {entry.withdrawn_note}
              </Text>
            )}
          </DescriptionList.Item>
        )}
      </DescriptionList>

      <section className={styles.section}>
        <FormSectionLabel>Soutěžící ({entry.competitors.length})</FormSectionLabel>
        <Table<EntryCompetitor>
          rows={entry.competitors}
          rowKey={(c) => c.team_membership_id}
          columns={[
            {
              key: "name",
              header: "Jméno",
              // Admins open the person detail dialog over this one; for
              // anybody else PersonLink is the plain name.
              render: (c) => (
                <PersonLink personId={c.person.id}>
                  {c.person.first_name} {c.person.last_name}
                </PersonLink>
              ),
            },
            {
              key: "birth",
              header: "Datum narození",
              render: (c) => formatDate(c.person.birth_date),
            },
            {
              key: "kind",
              header: "Registrovaný",
              align: "right",
              // Who is NOT registered is still the thing worth seeing here
              // — a guest may compete at a pohárovka and nowhere else — and
              // a column of ticks with a gap in it says that faster than a
              // column of chips reading „Člen" over and over. The gap is
              // the signal; the entry's own validation is what refuses a
              // guest on an MČR start.
              render: (c) => <RegisteredTick registered={c.kind === "registered"} />,
            },
          ]}
        />
      </section>

      <section className={styles.section}>
        <FormSectionLabel>Hudba</FormSectionLabel>
        <EntryMusicSection
          entry={entry}
          combo={combo}
          canUpload={canUploadMusic}
          disabledReason={musicDisabledReason}
          musicDeadline={musicDeadline}
          audience={audience}
        />
      </section>
    </div>
  );
}

/**
 * The three music sources are three different things to show, and the
 * switch is exhaustive on purpose — a fourth source must not fall
 * through to "the club owes a file".
 */
function EntryMusicSection({
  entry,
  combo,
  canUpload,
  disabledReason,
  musicDeadline,
  audience,
}: {
  entry: Entry;
  combo?: ComboRef;
  canUpload: boolean;
  disabledReason?: string;
  musicDeadline?: Dayjs;
  audience: MusicAudience;
}) {
  switch (entry.music_source) {
    case "own": {
      // An odhláška's recording stays readable — it is a record of what
      // was entered — but nothing is uploaded against it.
      const uploadable = canUpload && entry.status === "submitted";
      return (
        <>
          <OwnMusic
            entry={entry}
            canUpload={uploadable}
            disabledReason={disabledReason}
            audience={audience}
          />
          {uploadable && !entry.music && musicDeadline && (
            <Text as="div" variant="muted">
              Hudbu stačí nahrát do uzávěrky hudby {formatDateTime(musicDeadline)}.
            </Text>
          )}
        </>
      );
    }
    case "federation":
      return <FederationMusic combo={combo} />;
    case "none":
      // The club reads the same „Hudba ČNSMS“ as for `federation`: either
      // way it owes no file. The improvizace detail is the organizer's.
      if (audience === "team") return <Badge variant="info">Hudba ČNSMS</Badge>;
      return (
        <>
          <Badge variant="info">Zajišťuje zvukař</Badge>
          <Text as="div" variant="muted">
            Improvizace: skladbu vybírá zvukař na místě, žádná nahrávka se neodevzdává.
          </Text>
        </>
      );
  }
}

// A component of its own because useUploadEntryMusic is keyed to the
// entry id: calling it in the switch above would be a conditional hook.
function OwnMusic({
  entry,
  canUpload,
  disabledReason,
  audience,
}: {
  entry: Entry;
  canUpload: boolean;
  disabledReason?: string;
  audience: MusicAudience;
}) {
  const upload = useUploadEntryMusic(entry.id);
  return (
    <MusicPanel
      src={entryMusicUrl(entry.id)}
      music={entry.music}
      disabled={!canUpload}
      disabledReason={disabledReason}
      audience={audience}
      upload={upload}
    />
  );
}

/**
 * The official ČNSMS track, resolved PER COMBINATION — one discipline can
 * need more than one, so this reads combo.recording and never the
 * discipline. A federation discipline with no recording on file is a
 * rule set somebody has to finish, and it blocks the zvukař export, so
 * it says so here rather than showing an empty player.
 */
function FederationMusic({ combo }: { combo?: ComboRef }) {
  const recording = combo?.recording;
  if (!recording) {
    return (
      <>
        <Badge variant={musicBadge("federation", false).variant}>
          {musicBadge("federation", false).label}
        </Badge>
        <Alert variant="warn" title="Chybí oficiální nahrávka ČNSMS">
          Sada pravidel k této kombinaci zatím nemá nahrávku. Bez ní nejde sestavit
          archiv pro zvukaře; doplní ji správce sady pravidel.
        </Alert>
      </>
    );
  }
  return (
    <>
      <MusicPlayer key={String(recording.updated_at)} src={recordingFileUrl(recording.id)} />
      <MusicFileSummary
        name={recording.name}
        durationMs={recording.duration_ms}
        bitrateKbps={recording.bitrate_kbps}
        sizeBytes={recording.size_bytes}
        note="Oficiální nahrávka ČNSMS. Tým hudbu neodevzdává."
      />
    </>
  );
}
